<template>
  <div class="tab-pane show" id="registration" role="tabpanel" aria-labelledby="registration-tab">
    <form class="registration-form" @submit.prevent="submitRegistration">
      <div class="form-grid">
        
        <div class="form-group">
          <label for="fullName">Full name (As per CNIC) <span class="required-indicator">*</span></label>
          <input id="fullName" type="text" v-model="formData.fullName" class="form-control" placeholder="Full name"
            required />
        </div>

        <div class="form-group">
          <label for="fatherName">Father name (As per CNIC) <span class="required-indicator">*</span></label>
          <input id="fatherName" type="text" v-model="formData.fatherName" class="form-control"
            placeholder="Father name" required />
        </div>

        <div class="form-group">
          <label for="email">Email <span class="required-indicator">*</span></label>
          <input id="email" type="email" v-model="formData.email" class="form-control" placeholder="Email" required />
        </div>

        <div class="form-group">
          <label for="password">Password <span class="required-indicator">*</span></label>
          <input id="password" type="password" v-model="formData.password" class="form-control" placeholder="Password"
            required />
          <ul v-if="formData.password.length > 0 && passwordValidation.errors.length > 0"
            class="form-control bg-danger-light error-box"
            style="height: auto; margin-top: 8px; list-style-type: none; padding: 10px;">
            <li v-for="error in rules" :key="error.regex">
              <span v-if="passwordValidation.errors.find(x => x.regex === error.regex)" class="text-danger">
                <i class="fa fa-times"></i>&nbsp;&nbsp;&nbsp;{{ error.message }}
              </span>
              <span v-else class="text-success">
                <i class="fa fa-check"></i>&nbsp;&nbsp;{{ error.message }}
              </span>
            </li>
          </ul>
        </div>

        <div class="form-group">
          <label for="phone">Contact Number (Must be available on WhatsApp) <span class="required-indicator">*</span></label>
          <vue-tel-input v-model="phoneInput" :inputOptions="{ id: 'phone', placeholder: 'Phone' }"
            :validCharactersOnly="true" @validate="validatePhone" :maxLen="15" mode="international"
            :preferredCountries="['PK']" :enabledCountryCode="true" :enabledFlags="true" :inputClasses="'form-control'"
            :dropdownOptions="{
              showDialCodeInSelection: true,
              showFlags: true,
              showCountryNameInSelection: true,
            }" @input="updatePhoneData" required></vue-tel-input>
        </div>

        
        <template v-for="field in extraFields">
          
          <div v-if="field.Type === 'Text'" :key="field.Key" class="form-group" :class="{ 'full-width': field.Key === 'address' }">
            <label :for="field.Key">{{ field.DisplayName }} <span v-if="field.IsRequired" class="required-indicator">*</span></label>
            <input :id="field.Key" type="text" v-model="formData[field.Key]" class="form-control"
              :placeholder="field.DisplayName" :required="field.IsRequired"
              :maxlength="getPropertyValue(field.Properties, 'maxlength')"
              :readonly="getPropertyValue(field.Properties, 'readonly')" />
          </div>

          
          <div v-else-if="field.Type === 'DOB'" :key="'Dob'" class="form-group">
            <label :for="field.Key">{{ field.DisplayName }} <span v-if="field.IsRequired" class="required-indicator">*</span></label>
            <input :id="field.Key" type="date" v-model="formData[field.Key]" class="form-control"
              placeholder="dd/mm/yyyy" :required="field.IsRequired" />
          </div>

          <div v-else-if="field.Type === 'Select'" class="form-group">
            <label :for="field.Key">{{ field.DisplayName }} <span v-if="field.IsRequired" class="required-indicator">*</span></label>
            <div class="select-wrapper">
              <select :id="field.Key" v-model="formData[field.Key]" class="form-control" :required="field.IsRequired">
                <option value="" disabled selected>Select {{ field.DisplayName.toLowerCase() }}</option>
                <option v-for="option in field.SelectOptions" :key="option.value" :value="option.value">
                  {{ option.DisplayName }}
                </option>
              </select>
              <span class="select-arrow">&#9662;</span>
            </div>
          </div>

          <div v-else-if="field.Type === 'Radio'" class="form-group">
            <label>{{ field.DisplayName }} <span v-if="field.IsRequired" class="required-indicator">*</span></label>
            <div class="radio-group" :class="{ 'radio-buttons': field.radioStyle === 'buttons' }">
              <div v-for="option in field.RadioButtonItems" :key="option.value" class="radio-item">
                <input type="radio" :id="`${field.Key}-${option.value}`" :name="field.Key" :value="option.value"
                  v-model="formData[field.Key]" :required="field.IsRequired" />
                <label :for="`${field.Key}-${option.value}`">{{ option.DisplayName }}</label>
              </div>
            </div>
          </div>
        </template>

        <div v-if="!hasFieldType('city')" class="form-group">
          <label for="city">Select city <span class="required-indicator">*</span></label>
          <div class="select-wrapper">
            <select id="city" v-model="formData.city" class="form-control" @change="updateClassMode" required>
              <option value="" disabled selected>Select city</option>
              <option v-for="city in cities" :key="city" :value="city">
                {{ city }}
              </option>
            </select>
            <span class="select-arrow">&#9662;</span>
          </div>
        </div>
      </div>

      <div class="form-note">
        <span class="required-indicator">*</span> Indicates required fields
      </div>

      <div class="form-group full-width">
        <button type="submit" class="submit-button" :disabled="!isPasswordValid || isSubmitting">
          <span v-if="isSubmitting" class="loading-spinner"></span>
          {{ isSubmitting ? 'Processing...' : 'Register' }}
        </button>
      </div>
    </form>
  </div>
</template>
  
<script>
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import getCountryISO3 from "country-iso-2-to-3";
import { mapActions, mapState } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "RegistrationForm",
  components: { VueTelInput },
  data() {
    return {
      formData: {
        fullName: "",
        fatherName: "",
        email: "",
        phone: "",
        phoneCountryCode: "",
        password: "",
        username: "",
        city: "",
      },
      errors: {
        fullName: "",
        fatherName: "",
        email: "",
        phone: "",
        password: "",
      },
      phoneInput: "",
      isPhoneValid: false,
      isSubmitting: false,
      cities: [
              "Karachi",
              "Lahore",
              "Islamabad",
              "Rawalpindi",
              "Faisalabad",
              "Multan",
              "Peshawar",
              "Quetta",
              "Sialkot",
              "Gujranwala",
              "Hyderabad",
              "Sukkur",
              "Bahawalpur",
              "Sargodha",
              "Abbottabad",
              "Mardan",
              "Larkana",
              "Mirpur (AJK)",
              "Nawabshah",
              "Gilgit",
              "Sheikhupura",
              "Jhelum",
              "Gujrat",
              "Chiniot",
              "Okara",
              "Kasur",
              "Vehari",
              "Dera Ghazi Khan",
              "Rahim Yar Khan",
              "Mansehra",
              "Swat (Mingora)",
              "Kohat",
              "Bannu",
              "Chaman",
              "Gwadar",
              "Turbat",
              "Khuzdar",
              "Muzaffarabad (AJK)",
              "Kotli (AJK)",
              "Bahawalnagar",
              "Mianwali",
              "Kamalia",
              "Gojra",
              "Murree",
              "Narowal",
              "Hafizabad",
              "Pakpattan",
              "Kot Addu",
              "Shakargarh",
              "Bhakkar",
              "Mandi Bahauddin",
              "Sahiwal",
              "Layyah",
              "Toba Tek Singh",
              "Renala Khurd",
              "Pattoki",
              "Jaranwala",
              "Arifwala",
              "Mirpur Khas",
              "Umerkot",
              "Badin",
              "Dadu",
              "Khairpur",
              "Shikarpur",
              "Jacobabad",
              "Tando Allahyar",
              "Thatta",
              "Kotri",
              "Kashmore",
              "Tando Muhammad Khan",
              "Matiari",
              "Sanghar",
              "Ghotki",
              "Qambar Shahdadkot",
              "Hala",
              "Dera Ismail Khan",
              "Swabi",
              "Charsadda",
              "Lakki Marwat",
              "Karak",
              "Timergara",
              "Hangu",
              "Battagram",
              "Haripur",
              "Nowshera",
              "Shangla",
              "Malakand",
              "Chitral",
              "Upper Dir",
              "Lower Dir",
              "Sibi",
              "Hub",
              "Loralai",
              "Dera Murad Jamali",
              "Zhob",
              "Kharan",
              "Naseerabad",
              "Killa Abdullah",
              "Pishin",
              "Kalat",
              "Mastung",
              "Usta Mohammad",
              "Jafarabad",
              "Awaran",
              "Panjgur",
              "Washuk",
              "Skardu",
              "Hunza",
              "Chilas",
              "Ghanche",
              "Gupis",
              "Astore",
              "Nagar",
              "Rawalakot",
              "Bagh",
              "Neelum",
              "Bhimber",
              "Sudhnoti",
              "Haveli",
              "Hattian Bala",
              "Shigar",
              "Kharmang",
              "Kandiaro",
              "Bhit Shah",
              "Sehwan",
              "Moro",
              "Kot Diji",
          ],
      rules: [
        { regex: "^.{8,}$", message: "Password must be at least 8 characters long" },
        { regex: "[A-Z]", message: "Password must contain at least one uppercase letter" },
        { regex: "[a-z]", message: "Password must contain at least one lowercase letter" },
        { regex: "[0-9]", message: "Password must contain at least one number" },
        { regex: "[^A-Za-z0-9]", message: "Password must contain at least one special character" }
      ],
      extraFields: []
    };
  },
  computed: {
    ...mapState("appState", [
      "systemParameters",
      "loading",
      "currentCountry",
      "currentCity",
    ]),
    ...mapState("user", ["userCredentials", "loggedIn"]),

    passwordValidation() {
      let errors = [];
      for (let condition of this.rules) {
        if (!new RegExp(condition.regex).test(this.formData.password)) {
          errors.push(condition);
        }
      }
      if (errors.length === 0) {
        return { valid: true, errors };
      } else {
        return { valid: false, errors };
      }
    },

    isPasswordValid() {
      return this.formData.password.length === 0 || this.passwordValidation.valid;
    }
  },
  methods: {
    ...mapActions("user", ["register", "login", "generateVerificationID"]),

    // Check if a specific field type exists in extraFields
    hasFieldType(key) {
      return this.extraFields.some(field => field.Key === key);
    },

    // Parse JSON properties string
    getPropertyValue(propertiesStr, key) {
      try {
        if (!propertiesStr) return null;
        const properties = JSON.parse(propertiesStr);
        return properties[key];
      } catch (error) {
        console.error("Error parsing properties:", error);
        return null;
      }
    },

    validateForm() {
      let isValid = true;
      this.clearErrors();

      if (!this.formData.fullName) {
        this.errors.fullName = "Full name is required";
        isValid = false;
      }

      if (!this.formData.fatherName) {
        this.errors.fatherName = "Father name is required";
        isValid = false;
      }

      if (!this.formData.email) {
        this.errors.email = "Email is required";
        isValid = false;
      } else if (!this.validateEmail(this.formData.email)) {
        this.errors.email = "Please enter a valid email address";
        isValid = false;
      }

      if (!this.formData.phone || !this.isPhoneValid) {
        this.errors.phone = "Please enter a valid phone number";
        isValid = false;
      }

      if (!this.formData.password) {
        this.errors.password = "Password is required";
        isValid = false;
      } else if (!this.passwordValidation.valid) {
        this.errors.password = "Password does not meet the requirements";
        isValid = false;
      }

      // Validate required fields from extraFields
      for (const field of this.extraFields) {
        if (field.IsRequired && !this.formData[field.Key]) {
          isValid = false;
          // Could add specific error messages for each field if needed
        }
      }

      return isValid;
    },

    clearErrors() {
      Object.keys(this.errors).forEach((key) => {
        this.errors[key] = "";
      });
    },

    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    async submitRegistration() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.isSubmitting = true;
        this.setUsername();

        // Prepare custom fields from dynamic fields
        const customFields = {};
        
        // Add all dynamic fields to customFields
        for (const field of this.extraFields) {
          if (this.formData[field.Key] !== undefined) {
            // Format date if it's a DOB field
            if (field.Type === 'DOB') {
              customFields[field.Key] = this.formatDate(this.formData[field.Key]);
            } else {
              customFields[field.Key] = this.formData[field.Key];
            }
          }
        }

        let countryCodeiso3 = "PAK";

        if (
          this.formData.phoneCountryCode &&
          this.formData.phoneCountryCode.toLowerCase() !== "pk"
        ) {
          try {
            countryCodeiso3 = getCountryISO3(this.formData.phoneCountryCode.toUpperCase());
          } catch (error) {
            countryCodeiso3 = this.formData.phoneCountryCode.toUpperCase();
          }
        }

        const apiPayload = {
          firstName: this.formData.fullName,
          lastName: this.formData.fatherName,
          mobile: this.formData.phone,
          email: this.formData.email,
          username: this.formData.username,
          password: this.formData.password,
          countryCode: countryCodeiso3,
          city: this.formData.city,
          extraproperties: JSON.stringify({
            CustomFields: customFields
          })
        };

        let response = await this.register(apiPayload);

        if (!response) return;

        await this.loginAndNavigateToEnrollmentPage({
          username: this.formData.username,
          password: this.formData.password,
        });

      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: error.message || "Unknown error occurred",
          confirmButtonColor: "#d33",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async loginAndNavigateToEnrollmentPage({ username, password }) {
      try {
        await this.login({ username, password });

        const user = JSON.parse(localStorage.getItem("user"));

        const nextRoute =
          this.$route.query.nextUrl ||
          (user.type.toLowerCase() === "management" || user.type.toLowerCase() === "trainer"
            ? "/management/course"
            : "/sbil-enrollment-form");

        this.$router.push(nextRoute);

        this.$toasted.success(`Registration successful, Welcome ${user.firstName} !`, {
          theme: "outline",
          position: "bottom-center",
          duration: 3000,
        });

        this.userCredentials.username = null;
        this.userCredentials.password = null;

      } catch (error) {
        this.$toasted.error(error.message,
          {
            icon: "exclamation-triangle",
            theme: "outline",
            position: "bottom-center",
            action: {
              text: "DISMISS",
              onClick: (e, toastObject) => {
                toastObject.goAway(0);
              },
            },
          }
        );
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    },

    resetForm() {
      // Reset standard fields
      this.formData = {
        fullName: "",
        fatherName: "",
        email: "",
        phone: "",
        phoneCountryCode: "",
        password: "",
        username: "",
        city: "",
      };
      
      // Reset dynamic fields
      for (const field of this.extraFields) {
        this.formData[field.Key] = "";
      }
      
      this.phoneInput = "";
      this.isPhoneValid = false;
      this.clearErrors();
    },

    validatePhone(validationResult) {
      this.isPhoneValid = validationResult.valid || false;
      if (this.isPhoneValid) {
        this.formData.phone = validationResult.formatInternational;
        this.formData.phoneCountryCode = validationResult.countryCode;
      } else {
        this.formData.phone = "";
        this.formData.phoneCountryCode = "";
      }
    },

    updatePhoneData(formattedNumber, phoneObject) {
      this.formData.phone = formattedNumber;

      if (phoneObject && Object.keys(phoneObject).length > 0) {
        this.formData.phoneCountryCode = phoneObject.country.iso2
        this.isPhoneValid = phoneObject.valid || false;
      }
    },

    setUsername() {
      if (this.formData.fullName && this.formData.fatherName) {
        const length = 4;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let randomCode = "";

        for (let i = 0; i < length; i++) {
          randomCode += charset.charAt(Math.floor(Math.random() * charset.length));
        }

        this.formData.username =
          this.formData.fullName.trim().replace(/ +/g, "_").toLowerCase() +
          "_" +
          this.formData.fatherName.trim().replace(/ +/g, "_").toLowerCase() +
          "_" +
          randomCode;
      }
    },

    updateClassMode() {
      const hasClassModeField = this.extraFields.some(field => field.Key === 'classMode');
      
      if (hasClassModeField && this.formData.city) {
        this.formData.classMode =
          this.formData.city.toLowerCase() === "karachi" ? "Physical" : "Online";
      }
    },

    formatCNIC(event) {
      let value = event.target.value.replace(/[^0-9]/g, "");
      if (value.length > 13) {
        value = value.slice(0, 13);
      }
      const parts = [value.slice(0, 5), value.slice(5, 12), value.slice(12)];
      this.formData.cnicNo = parts.filter(Boolean).join("-");
    },

    // Load dynamic fields from system parameters
    loadExtraFields() {
      try {
        if (this.systemParameters && this.systemParameters.UserRegisterExtraFields) {

          const extraFieldsConfig = this.systemParameters.UserRegisterExtraFields;
          this.extraFields = extraFieldsConfig;
          
          for (const field of this.extraFields) {
            this.formData[field.Key] = "";
            
            if (field.Type === 'Radio' && field.RadioButtonItems) {
              const checkedOption = field.RadioButtonItems.find(item => item.checked);
              if (checkedOption) {
                this.formData[field.Key] = checkedOption.value;
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading extra fields:", error);
        this.extraFields = [];
      }
    }
  },
  created() {
    if (this.loggedIn) {
      this.$router.replace("/sbil-enrollment-form");
    }

    this.formData.phoneCountryCode = this.currentCountry;
    
    this.loadExtraFields();
    
    if (this.currentCity) {
      this.formData.city = this.currentCity;
      this.updateClassMode();
    }
    
    if (this.systemParameters && this.systemParameters.PasswordValidationRules) {
      try {
        this.rules = JSON.parse(this.systemParameters.PasswordValidationRules);
      } catch (error) {
        console.error("Error parsing password validation rules:", error);
      }
    }
  }
};
</script>
  
<style scoped>
.registration-form {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.full-width {
  grid-column: 1 / -1;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #0078d7;
  font-weight: 500;
  font-size: 14px;
}

.required-indicator {
  color: #e74c3c;
  font-weight: bold;
  margin-left: 2px;
}

.form-note {
  margin: 15px 0;
  font-size: 14px;
  color: #666;
}

.form-control {
  width: 100%;
  padding: 12px !important;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
}

.select-wrapper {
  position: relative;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #888;
}

.form-control {
  appearance: none;
  padding-right: 30px;
  border-color: #8dc63f !important;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
}

.submit-button {
  background-color: #0d6db7;
  color: white;
  width: 100%;
  border: none;
  padding: 8px 24px;
  font-size: 16px;
  margin-top: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.submit-button:hover {
  background-color: #005fa3;
}

.submit-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.vue-tel-input {
  border-color: #8dc63f !important;
  padding: 15px;
}

.bg-danger-light {
  background-color: #fff8f8;
  border-color: #ffcdd2 !important;
}

.error-box {
  padding: 10px;
  margin-top: 8px;
}

.text-danger {
  color: #f44336;
}

.text-success {
  color: #4caf50;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-buttons {
  flex-direction: row;
  gap: 15px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .radio-buttons {
    flex-direction: column;
  }
}
</style>